/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * The actions for page ExpensesAnalyzer
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.1.0
 */

import { IChartDataInput } from '../../components/common/Chart/chartTypes';
import { createAction } from '../../modules/app/createAction';
import { EExpensesAnalyzerChart } from './components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import {
    EXPENSES_ANALYZER_FULLED_ACCOUNTS_LIST,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_FAILED,
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_SUCCESS,
    EXPENSES_ANALYZER_INITIALIZE_CHART,
    EXPENSES_ANALYZER_LOAD_CHART,
    EXPENSES_ANALYZER_LOAD_CHART_ERROR,
    EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL,
    EXPENSES_ANALYZER_REDIRECT_CALLS_DETAIL,
    EXPENSES_ANALYZER_REDIRECT_DAILY_EXPENSES,
    EXPENSES_ANALYZER_REDIRECT_SUBSCRIBERS_EXPENSES,
    EXPENSES_ANALYZER_SET_CURRENT_ACCOUNT,
    EXPENSES_ANALYZER_SET_CURRENT_CHART,
    EXPENSES_ANALYZER_SET_SELECTED_ID,
    EXPENSES_ANALYZER_SHOW_GRAPH,
    EXPENSES_ANALYZER_UPDATE_CHART_ITEM,
    EXPENSES_ANALYZER_UPDATE_CHART_ITEM_SUCCESSFUL,
    SET_EXPENSES_ANALYZER_LOADING_CHART,
    SET_EXPENSES_ANALYZER_TYPE,
} from './pageExpensesAnalyzerConstants';
import {
    ILoadChartExpensesAnalyzerSuccessfulPayload,
    TFulledExpensesAccountsListAction,
    TGetExpensesAccountsListAction,
    TGetExpensesAccountsListFailed,
    TGetExpensesAccountsListSuccess,
    TInitializeChartExpensesAnalyzerAction,
    TLoadChartExpensesAnalyzerAction,
    TLoadChartExpensesAnalyzerErrorAction,
    TLoadChartExpensesAnalyzerSuccessfulAction,
    TSetCurrentAccountExpensesAnalyzerAction,
    TSetCurrentChartExpensesAnalyzerAction,
    TSetExpensesAnalyzerAction,
    TSetExpensesAnalyzerLoadingChart,
    TSetSelectedIdExpensesAnalyzerAction,
    TShowExpensesAnalyzerGraphAction,
    TUpdateChartExpensesAnalyzerAction,
    TUpdateChartExpensesAnalyzerSuccessfulAction,
} from './types/pageExpensesAnalyzerActionTypes';
import { EExpensesAnalyzerTypes, IExpensesAccountsListItem } from './types/pageExpensesAnalyzerTypes';

/*
* -------------------------------------------
*           SHOW GRAPH
* -------------------------------------------
*/

export const expensesAnalyzerShowGraph = (payload: boolean): TShowExpensesAnalyzerGraphAction =>
    createAction(EXPENSES_ANALYZER_SHOW_GRAPH, payload);

/*
* -------------------------------------------
*        SET CURRENT ACCOUNT
* -------------------------------------------
*/

export const setCurrentAccountExpensesAnalyzerAction =
    (payload: number): TSetCurrentAccountExpensesAnalyzerAction =>
        createAction(EXPENSES_ANALYZER_SET_CURRENT_ACCOUNT, payload);

export const setSelectedIdExpensesAnalyzerAction =
    (payload: number): TSetSelectedIdExpensesAnalyzerAction =>
        createAction(EXPENSES_ANALYZER_SET_SELECTED_ID, payload);

/*
* -------------------------------------------
*        SET CURRENT CHART
* -------------------------------------------
*/

export const setCurrentChartExpensesAnalyzerAction =
    (payload: EExpensesAnalyzerChart): TSetCurrentChartExpensesAnalyzerAction =>
        createAction(EXPENSES_ANALYZER_SET_CURRENT_CHART, payload);

/*
* -------------------------------------------
*        SET ANALYZER TYPE
* -------------------------------------------
*/

export const setExpensesAnalyzerType = (payload: EExpensesAnalyzerTypes): TSetExpensesAnalyzerAction =>
    createAction(SET_EXPENSES_ANALYZER_TYPE, payload);

/*
* -------------------------------------------
*        LOAD CHART
* -------------------------------------------
*/

export const loadChartsExpensesAnalyzerAction = (): TLoadChartExpensesAnalyzerAction =>
    createAction(EXPENSES_ANALYZER_LOAD_CHART);

export const loadChartsExpensesAnalyzerSuccessfulAction =
    (payload: ILoadChartExpensesAnalyzerSuccessfulPayload): TLoadChartExpensesAnalyzerSuccessfulAction =>
        createAction(EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL, payload);

export const loadChartsExpensesAnalyzerErrorAction =
    (payload: string): TLoadChartExpensesAnalyzerErrorAction =>
        createAction(EXPENSES_ANALYZER_LOAD_CHART_ERROR, payload);

/*
* -------------------------------------------
*        UPDATE CHART
* -------------------------------------------
*/

export const updateChartItemExpensesAnalyzerAction =
    (payload: IChartDataInput): TUpdateChartExpensesAnalyzerAction =>
        createAction(EXPENSES_ANALYZER_UPDATE_CHART_ITEM, payload);

export const updateChartItemExpensesAnalyzerSuccessfulAction =
    (payload: IChartDataInput[]): TUpdateChartExpensesAnalyzerSuccessfulAction =>
        createAction(EXPENSES_ANALYZER_UPDATE_CHART_ITEM_SUCCESSFUL, payload);

/*
* -------------------------------------------
*        INITIALIZE CHART
* -------------------------------------------
*/

export const initializeChartExpensesAnalyzerAction = (): TInitializeChartExpensesAnalyzerAction =>
    createAction(EXPENSES_ANALYZER_INITIALIZE_CHART);

/*
* -------------------------------------------
*        REDIRECT
* -------------------------------------------
*/

export const expensesAnalyzerRedirectSubscribersExpenses = () =>
    createAction(EXPENSES_ANALYZER_REDIRECT_SUBSCRIBERS_EXPENSES);

export const expensesAnalyzerRedirectDailyExpenses = () =>
    createAction(EXPENSES_ANALYZER_REDIRECT_DAILY_EXPENSES);

export const expensesAnalyzerRedirectCallsDetail = () =>
    createAction(EXPENSES_ANALYZER_REDIRECT_CALLS_DETAIL);

/*
* -------------------------------------------
*        GET ACCOUNTS LIST
* -------------------------------------------
*/

export const fulledExpensesAccountsListAction = (): TFulledExpensesAccountsListAction =>
    createAction(EXPENSES_ANALYZER_FULLED_ACCOUNTS_LIST);

export const getExpensesAccountsListAction = (payload = false): TGetExpensesAccountsListAction =>
    createAction(EXPENSES_ANALYZER_GET_ACCOUNTS_LIST, payload);

export const getExpensesAccountsListSuccess = (payload: IExpensesAccountsListItem[]): TGetExpensesAccountsListSuccess =>
    createAction(EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_SUCCESS, payload);

export const getExpensesAccountsListFailed = (payload: string): TGetExpensesAccountsListFailed =>
    createAction(EXPENSES_ANALYZER_GET_ACCOUNTS_LIST_FAILED, payload);

export const setExpensesAnalyzerLoadingChartAction = (payload: boolean): TSetExpensesAnalyzerLoadingChart =>
    createAction(SET_EXPENSES_ANALYZER_LOADING_CHART, payload);
